import { useState, useEffect } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { UserIdentity, UserRole, groupColor, GROUP_PRIORITY } from './types';

export function useUserIdentity(): {
  identity: UserIdentity | null;
  loading: boolean;
} {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [identity, setIdentity] = useState<UserIdentity | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const profile = await identityApi.getProfileInfo();
        const backstageIdentity = await identityApi.getBackstageIdentity();

        const ownershipRefs = backstageIdentity.ownershipEntityRefs ?? [];
        const groupRefs = ownershipRefs.filter(ref => ref.startsWith('group:'));

        // Fetch group entities in parallel to get displayName
        const groupEntities = await Promise.all(
          groupRefs.map(async ref => {
            try {
              const entity = await catalogApi.getEntityByRef(ref);
              return { ref, entity };
            } catch {
              return { ref, entity: null };
            }
          }),
        );

        const roles: UserRole[] = groupEntities
          .filter(g => g.entity != null)
          .map(({ ref, entity }) => {
            const groupName = ref.split('/').pop() || ref;
            const displayName =
              (entity!.spec as Record<string, any>)?.profile?.displayName ||
              entity!.metadata.name;
            return {
              name: displayName,
              groupRef: ref,
              groupName,
              color: groupColor(groupName),
            };
          });

        if (roles.length === 0) {
          roles.push({
            name: 'Guest',
            groupRef: '',
            groupName: '',
            color: groupColor(''),
          });
        }

        // Sort by priority and pick the most elevated
        roles.sort((a, b) => {
          const ai = GROUP_PRIORITY.indexOf(a.groupName);
          const bi = GROUP_PRIORITY.indexOf(b.groupName);
          const ap = ai === -1 ? GROUP_PRIORITY.length : ai;
          const bp = bi === -1 ? GROUP_PRIORITY.length : bi;
          return ap - bp;
        });

        const elevatedRole = roles[0];

        const displayName = profile.displayName || 'User';
        const parts = displayName.split(' ');
        const initials =
          parts.length >= 2
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
            : displayName.substring(0, 2).toUpperCase();

        if (!cancelled) {
          setIdentity({
            displayName,
            email: profile.email || '',
            initials,
            roles,
            elevatedRole,
          });
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi]);

  return { identity, loading };
}
